import type { LoanProductDetail, LoanProductFee, LoanProductPenaltyRule } from '@/api/loans-types'
import { MoneyDisplay } from '@/components/money-display'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'

import {
  EARLY_SETTLEMENT_LABELS,
  FEE_CALCULATION_LABELS,
  FEE_COLLECTION_LABELS,
  INTEREST_METHOD_LABELS,
  INTEREST_PERIOD_LABELS,
  MONTH_LENGTH_LABELS,
  PENALTY_CALCULATION_LABELS,
  REPAYMENT_FREQUENCY_LABELS,
  formatRate,
} from './constants'
import { Field, Section } from './panel-primitives'

/**
 * A product, read.
 *
 * The one rule this screen exists to keep is that a fee's `value` means two different things and
 * has to be rendered as one or the other. `FlatAmount` is money in minor units and goes through
 * `MoneyDisplay`; `PercentOfPrincipal` is a fraction and is rendered as a percentage. Rendering
 * either as the other is the difference between a GHS 5.00 processing fee and a five-pesewa one,
 * and it is the mistake this file is written to make impossible.
 */
function FeeValue({ fee, currency }: { fee: LoanProductFee; currency: string }) {
  return fee.calculation === 'FlatAmount' ? (
    <MoneyDisplay value={fee.value} currency={currency} />
  ) : (
    <span className="numeric block text-right tabular-nums">{formatRate(fee.value)}</span>
  )
}

function PenaltyValue({ rule, currency }: { rule: LoanProductPenaltyRule; currency: string }) {
  return rule.calculation === 'FlatPerMissedInstalment' ? (
    <MoneyDisplay value={rule.value} currency={currency} />
  ) : (
    <span className="numeric block text-right tabular-nums">{formatRate(rule.value)}</span>
  )
}

export function LoanProductPanels({ product }: { product: LoanProductDetail }) {
  const currency = product.currencyCode

  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <Section title="Pricing" description="Copied onto a round at application, frozen at approval.">
        <dl className="grid grid-cols-2 gap-x-6 gap-y-4">
          <Field label="Code" value={product.code} mono />
          <Field label="Currency" value={product.currencyCode} />
          <Field label="Method" value={INTEREST_METHOD_LABELS[product.interestMethod]} />
          <Field
            label="Rate"
            value={`${formatRate(product.interestRate)} ${INTEREST_PERIOD_LABELS[product.interestRatePeriod]}`}
          />
          <Field label="Month length" value={MONTH_LENGTH_LABELS[product.monthLengthConvention]} />
          <Field
            label="Frequency"
            value={REPAYMENT_FREQUENCY_LABELS[product.repaymentFrequency]}
          />
          <Field
            label="Growth cap"
            value={formatRate(product.maxRoundPrincipalGrowthPercent, 0)}
            hint="How much larger the next round may be than the last."
          />
          <Field
            label="Early settlement"
            value={
              product.allowEarlySettlement
                ? product.earlySettlementRule === null
                  ? 'Allowed, with no rule named'
                  : EARLY_SETTLEMENT_LABELS[product.earlySettlementRule]
                : 'Not allowed'
            }
            hint={
              product.allowEarlySettlement && product.earlySettlementRule === null
                ? 'A settlement quote on this product assumes no rebate.'
                : undefined
            }
          />
        </dl>
      </Section>

      <Section title="Limits" description="Checked on every application and again at approval.">
        <dl className="grid grid-cols-2 gap-x-6 gap-y-4">
          <Field
            label="Smallest loan"
            value={
              <MoneyDisplay
                value={product.minPrincipalMinor}
                currency={currency}
                className="text-left"
              />
            }
          />
          <Field
            label="Largest loan"
            value={
              <MoneyDisplay
                value={product.maxPrincipalMinor}
                currency={currency}
                className="text-left"
              />
            }
          />
          <Field
            label="Instalments"
            value={`${product.minInstalments}–${product.maxInstalments}`}
          />
          <Field label="Grace instalments" value={product.graceInstalments} />
          <Field
            label="Grace days before penalty"
            value={product.graceDaysBeforePenalty}
            hint="Days past a due date before the ageing run charges anything."
          />
          <Field
            label="Security deposit"
            value={formatRate(product.securityDepositPercent, 0)}
            hint="Of principal. Not withdrawable; released by an offset or at closure."
          />
          <Field
            label="Compulsory savings"
            value={
              <MoneyDisplay
                value={product.compulsorySavingsPerInstalmentMinor}
                currency={currency}
                className="text-left"
              />
            }
            hint="Per instalment. Not part of what the loan owes."
          />
          <Field
            label="Group"
            value={product.requiresGroup ? 'Lends only through a group' : 'Individual lending'}
          />
          <Field
            label="Allocation order"
            value={product.allocationOrder.join(' → ')}
            hint="Within an instalment. The oldest instalment is always settled first."
          />
        </dl>
      </Section>

      <Section
        title="Fees"
        description="A flat fee is money; a percentage fee is a fraction of principal."
        className="lg:col-span-2"
      >
        {product.fees.length === 0 ? (
          <p className="text-xs text-muted">No fees. Loans on this product carry none.</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Fee</TableHead>
                <TableHead>Calculation</TableHead>
                <TableHead className="text-right">Value</TableHead>
                <TableHead>Collection</TableHead>
                <TableHead>Income account</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {product.fees.map((fee) => (
                <TableRow key={fee.id}>
                  <TableCell className="font-medium text-heading">{fee.name}</TableCell>
                  <TableCell>{FEE_CALCULATION_LABELS[fee.calculation]}</TableCell>
                  <TableCell>
                    <FeeValue fee={fee} currency={currency} />
                  </TableCell>
                  <TableCell>{FEE_COLLECTION_LABELS[fee.collection]}</TableCell>
                  <TableCell className="font-mono text-xs">{fee.feeIncomeAccountCode}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Section>

      <Section
        title="Penalty"
        description="What a missed instalment costs. Charged by the ageing run, never by hand."
        className="lg:col-span-2"
      >
        {product.penaltyRules.length === 0 ? (
          <p className="text-xs text-muted">
            No penalty rule. Late instalments on this product accrue nothing.
          </p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Trigger</TableHead>
                <TableHead>Calculation</TableHead>
                <TableHead className="text-right">Value</TableHead>
                <TableHead className="text-right">Cap</TableHead>
                <TableHead>Income account</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {product.penaltyRules.map((rule) => (
                <TableRow key={rule.id}>
                  <TableCell>Late instalment</TableCell>
                  <TableCell>{PENALTY_CALCULATION_LABELS[rule.calculation]}</TableCell>
                  <TableCell>
                    <PenaltyValue rule={rule} currency={currency} />
                  </TableCell>
                  <TableCell className="numeric text-right tabular-nums">
                    {rule.maxCapPercentOfPrincipal === null ? (
                      <span className="text-muted">Uncapped</span>
                    ) : (
                      formatRate(rule.maxCapPercentOfPrincipal, 0)
                    )}
                  </TableCell>
                  <TableCell className="font-mono text-xs">
                    {rule.penaltyIncomeAccountCode}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Section>

      <Section
        title="Ledger accounts"
        description="Where this product's money lands in the chart of accounts."
        className="lg:col-span-2"
      >
        <dl className="grid gap-x-6 gap-y-4 sm:grid-cols-3">
          <Field label="Principal" value={product.glMapping.principalAccountCode} mono />
          <Field label="Interest income" value={product.glMapping.interestIncomeAccountCode} mono />
          <Field label="Penalty income" value={product.glMapping.penaltyIncomeAccountCode} mono />
          <Field label="Fee income" value={product.glMapping.feeIncomeAccountCode} mono />
          <Field
            label="Write-off expense"
            value={product.glMapping.writeOffExpenseAccountCode}
            mono
          />
          <Field label="Allowance" value={product.glMapping.allowanceAccountCode} mono />
        </dl>
      </Section>
    </div>
  )
}
